import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

import { get, constant } from 'lodash/fp';

import GreenCheck from '../../themes/svg/greenCheck';
import CloseIcon from '../../themes/svg/closeIcon';

import { InlineTextEditorSpan, ErrorText, PillRow } from './styles';

export const useEnterEscapeKeyHandler = onClose => useCallback(
  (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      onClose(true);
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose(false);
    }
  },
  [onClose],
);

export const useCloseHandler = (onComplete, inputRef, validator, setError) => useCallback(
  (completed) => {
    const currentValue = get('current.value')(inputRef);
    const [isValid, errorMessage] = validator(currentValue);

    if (!completed || isValid) {
      onComplete(completed, currentValue);
    } else {
      setError(errorMessage);
    }
  },
  [onComplete, inputRef, validator],
);

export const useUpdateErrorMessage = (validator, error, setError) => useCallback(
  (event) => {
    if (error) {
      const [isValid, errorMessage] = validator(event.target.value);

      if (!isValid && error !== errorMessage) {
        setError(errorMessage);
      } else if (isValid) {
        setError('');
      }
    }
  },
  [validator, error],
);

const InlineTextEditor = ({
  value,
  onComplete,
  validator,
  ...inputProps
}) => {
  const inputRef = useRef(null);
  const [error, setError] = useState('');

  const close = useCloseHandler(onComplete, inputRef, validator, setError);

  const onKeyDown = useEnterEscapeKeyHandler(close);

  const updateErrorMessage = useUpdateErrorMessage(validator, error, setError);

  return (
    <InlineTextEditorSpan>
      <PillRow>
        <input
          {...inputProps}
          type="text"
          ref={inputRef}
          onChange={updateErrorMessage}
          defaultValue={value}
          onKeyDown={onKeyDown}
        />
        <span tabIndex="-1" role="button" onClick={() => close(true)}><GreenCheck /></span>
        <span tabIndex="0" role="button" onClick={() => close(false)}><CloseIcon /></span>
      </PillRow>
      <ErrorText>{error}</ErrorText>
    </InlineTextEditorSpan>
  );
};
InlineTextEditor.propTypes = {
  value: PropTypes.string.isRequired,
  onComplete: PropTypes.func.isRequired,
  validator: PropTypes.func,
};
InlineTextEditor.defaultProps = {
  validator: constant([true]),
};
export default InlineTextEditor;
